"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StateRules = exports.FederalRules = exports.JurisdictionRules = void 0;
const wasteAccumulation_1 = require("encamp-shared/dist/constants/wasteAccumulation");
/**
 * Note that these classes are meant to be singletons instantiated at module load;
 * they are classes because the partial-override ergonomics of TS classes fit the
 * domain we need to model surprisingly well.
 * They should never carry any instance state, only static constants and pure functions.
 */
/**
 * The contract a jurisdiction implements: three limit slots the concrete
 * `resolve` template composes, plus the hand-authored spec the sweep holds
 * the class to. Rules and spec travel on one object so they cannot be paired
 * wrong — see `StateRules` for what must and must not inherit.
 *
 * A jurisdiction answers exactly one question — "what is this facility's
 * STANDING limit?" — and has no episodic awareness whatsoever. Subpart L is a
 * parallel limit living in ../../episodic; the clock composes the two.
 */
class JurisdictionRules {
    constructor() {
        /**
         * Named determinations this jurisdiction computes from raw axes (see
         * `Derivation`). Defaulted empty rather than abstract: most jurisdictions
         * derive nothing and shouldn't have to say so. The cost is that a state
         * that authors a table but forgets to override this compiles — caught at
         * test time, when the sweep's expansion throws on the first row keyed on
         * the undeclared determination.
         */
        this.derivations = {};
    }
    resolve(ctx) {
        if (ctx.areaType === "CENTRAL") {
            return { areaType: "CENTRAL", clock: this.centralClock(ctx) };
        }
        return {
            areaType: "SATELLITE",
            caps: this.satelliteCaps(ctx),
            clock: this.satelliteClock(ctx),
        };
    }
}
exports.JurisdictionRules = JurisdictionRules;
/**
 * The specification `FederalRules` is tested against. The state-status axis
 * never informs a federal determination, and no federal rule reads the
 * waste-stream classification, so no row states either. Episodic generation
 * has its own table (../../episodic/expectations.ts) — these rows are the
 * facility's standing limits, which an event never changes.
 *
 * Numbers and citations are literal rather than imported: this table is what
 * pins the implementation's constants, so a change to one must fail here.
 */
const FEDERAL_SATELLITE_CAPS = {
    kind: "VOLUME",
    nonAcuteGal: 55,
    acuteGal: 0.25,
    acuteKg: 1,
    moveWindowDays: 3,
    citation: "40 CFR 262.15",
    jurisdiction: "FEDERAL",
};
/**
 * 262.15 imposes no standing satellite clock — the only time limit is the
 * move window once a cap is breached, carried as moveWindowDays on the caps.
 */
const NO_STANDING_SATELLITE_CLOCK = {
    kind: "NO_LIMIT",
    citation: "40 CFR 262.15",
    jurisdiction: "FEDERAL",
};
const UNIVERSAL_WASTE_NOT_MANAGED_NOTE = "A universal-waste category the generator's state never adopted is not universal waste there: " +
    'part 273\'s standards apply only "while managed as UW" (40 CFR 273.9, 262.13(c)(6)), which ' +
    "requires the state to have taken that category into its program. Spent aerosol cans in Kansas " +
    "are fully regulated hazardous waste, so these rows restate the part 262 answers deliberately, " +
    "the same way the non-participating pharmaceutical rows do. Whether the container qualifies is " +
    "read from EVERY line on its stream, not any one of them: a line carrying an unadopted category " +
    "keeps the whole stream on these rows. Measured against production over 1.1M waste lines, no " +
    "stream carries two different non-null kinds, so that all-or-any choice changes no answer today; " +
    "the 31 streams of 292,871 that mixed a real kind with an uncategorized line are separated by " +
    "stream identity before these rules are asked.";
const PHARMACEUTICAL_NON_PARTICIPATING_NOTE = "A facility not operating under Subpart P manages pharmaceutical waste under the ordinary " +
    "generator standards, so these rows restate the part 262 answers deliberately.";
const FEDERAL_EXPECTATIONS = [
    // ── CENTRAL ACCUMULATION ──────────────────────────────────────────────
    // VSQG — 40 CFR 262.14
    {
        when: {
            areaType: "CENTRAL",
            generatorStatus: "VSQG",
            // Narrowed to ordinary RCRA so the kind rows below have disjoint room
            // to claim their own central contexts — see the kind rows for what
            // governs a VSQG's universal-waste, used-oil, or pharmaceutical
            // containers instead.
            wasteKind: null,
        },
        expect: {
            clock: { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" },
        },
        note: "A generator meeting the VSQG conditions of 262.14 is not subject to the " +
            "central accumulation time limits of 262.16/262.17, and 262.14 imposes none of its own. " +
            "What constrains a VSQG is the facility-wide on-site quantity cap, tracked outside this " +
            "table.",
    },
    // SQG — 40 CFR 262.16
    {
        when: {
            areaType: "CENTRAL",
            generatorStatus: "SQG",
            mustTransport200Miles: false,
            // Narrowed to ordinary RCRA — see the kind rows below.
            wasteKind: null,
        },
        expect: {
            clock: { kind: "TIME", days: 180, citation: "40 CFR 262.16(b)", jurisdiction: "FEDERAL" },
        },
    },
    {
        when: {
            areaType: "CENTRAL",
            generatorStatus: "SQG",
            mustTransport200Miles: true,
            // Narrowed to ordinary RCRA — see the kind rows below.
            wasteKind: null,
        },
        expect: {
            clock: { kind: "TIME", days: 270, citation: "40 CFR 262.16(d)", jurisdiction: "FEDERAL" },
        },
    },
    // LQG — 40 CFR 262.17
    {
        when: {
            areaType: "CENTRAL",
            generatorStatus: "LQG",
            // Narrowed to ordinary RCRA — see the kind rows below.
            wasteKind: null,
        },
        expect: {
            clock: { kind: "TIME", days: 90, citation: "40 CFR 262.17(a)", jurisdiction: "FEDERAL" },
        },
    },
    // Unknown status
    {
        when: {
            areaType: "CENTRAL",
            generatorStatus: null,
            // Narrowed to ordinary RCRA — see the kind rows below.
            wasteKind: null,
        },
        expect: { clock: { kind: "UNDETERMINED" } },
    },
    // ── SATELLITE ACCUMULATION — 40 CFR 262.15 ────────────────────────────
    // One row per status rather than an omission, because the VSQG carve-out
    // splits the axis.
    {
        when: {
            areaType: "SATELLITE",
            generatorStatus: "SQG",
            // Narrowed to ordinary RCRA — see the kind rows below.
            wasteKind: null,
        },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: NO_STANDING_SATELLITE_CLOCK },
    },
    {
        when: {
            areaType: "SATELLITE",
            generatorStatus: "LQG",
            // Narrowed to ordinary RCRA — see the kind rows below.
            wasteKind: null,
        },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: NO_STANDING_SATELLITE_CLOCK },
    },
    {
        when: {
            areaType: "SATELLITE",
            generatorStatus: "VSQG",
            // Narrowed to ordinary RCRA — see the kind rows below.
            wasteKind: null,
        },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" },
            clock: NO_STANDING_SATELLITE_CLOCK,
        },
        note: "262.15 grants relief from the " +
            "central accumulation time limits of 262.16/262.17, which a VSQG is not subject to, and " +
            "262.14 never references 262.15 — so we read the satellite caps as not reaching VSQGs.",
    },
    {
        when: {
            areaType: "SATELLITE",
            generatorStatus: null,
            // Narrowed to ordinary RCRA — see the kind rows below.
            wasteKind: null,
        },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: NO_STANDING_SATELLITE_CLOCK },
        note: "The more protective default until the status is resolved.",
    },
    // ── KIND-SPECIFIC PROGRAMS ─────────────────────────────────────────────
    // Universal waste, used oil, and pharmaceutical containers are governed by
    // their own part of 40 CFR rather than part 262's generator-tier clocks —
    // status-independent, so these rows wildcard generatorStatus and the other
    // 262-specific axes.
    {
        when: {
            areaType: "CENTRAL",
            wasteKind: "UNIVERSAL_WASTE",
            managedAsUniversalWaste: true,
        },
        expect: {
            clock: {
                kind: "TIME",
                days: 365,
                citation: "40 CFR 273.15(a), 273.35(a)",
                jurisdiction: "FEDERAL",
            },
        },
        note: "273.15(a) (small-quantity handler) and 273.35(a) (large-quantity handler) are word for " +
            "word identical one-year limits. This engine does not model handler category, which is " +
            "set by universal waste quantity on hand rather than RCRA generator status, hence citing " +
            "both sections together.",
    },
    {
        when: {
            areaType: "SATELLITE",
            wasteKind: "UNIVERSAL_WASTE",
            managedAsUniversalWaste: true,
        },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 261.9, 273.1(b)", jurisdiction: "FEDERAL" },
            clock: {
                kind: "TIME",
                days: 365,
                citation: "40 CFR 273.15(a), 273.35(a)",
                jurisdiction: "FEDERAL",
            },
        },
        note: "Universal waste is excluded from part 262's hazardous waste requirements entirely, so " +
            "the SAA volume caps and move window never attach — only the one-year accumulation limit " +
            "does, and it runs the same whether the container sits in a satellite or central area.",
    },
    {
        when: {
            areaType: "CENTRAL",
            wasteKind: "USED_OIL",
        },
        expect: {
            clock: { kind: "NO_LIMIT", citation: "40 CFR 279.22", jurisdiction: "FEDERAL" },
        },
        note: "Part 279 sets no generator storage time limit for used oil — only a 35-day cap for " +
            "transfer facilities, which does not apply to a generator's own accumulation.",
    },
    {
        when: {
            areaType: "SATELLITE",
            wasteKind: "USED_OIL",
        },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 261.6(a)(4)", jurisdiction: "FEDERAL" },
            clock: { kind: "NO_LIMIT", citation: "40 CFR 279.22", jurisdiction: "FEDERAL" },
        },
        note: "Used oil is excluded from part 262 by 261.6(a)(4), so neither the caps nor a clock attach.",
    },
    // Pharmaceutical containers only get the Subpart P treatment AT A FACILITY
    // THAT OPERATES UNDER IT — the healthcare-facility activity, no
    // withdrawal, and a state that adopted the program (see
    // `operatesUnderSubpartP` on RuleQuery). Unlike universal waste and used
    // oil, Subpart P is facility-scoped, not just kind-scoped, so the
    // participating and non-participating rows below are genuinely different
    // legal regimes rather than one row wildcarding an irrelevant axis.
    {
        when: {
            areaType: "CENTRAL",
            wasteKind: "PHARMACEUTICAL",
            operatesUnderSubpartP: true,
        },
        expect: {
            clock: { kind: "TIME", days: 365, citation: "40 CFR 266.502(f)(1)", jurisdiction: "FEDERAL" },
        },
        note: "266.502(f)(1) is the healthcare-facility one-year limit for non-creditable " +
            "pharmaceuticals. Creditable pharmaceuticals have no facility accumulation clock at all, " +
            "but this engine's wasteKind does not distinguish creditable from non-creditable, so " +
            "applying one year to every pharmaceutical container is over-strict but never " +
            "under-strict — intentional until the data carries that distinction.",
    },
    {
        when: {
            areaType: "SATELLITE",
            wasteKind: "PHARMACEUTICAL",
            operatesUnderSubpartP: true,
        },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 266.501(d)", jurisdiction: "FEDERAL" },
            clock: { kind: "TIME", days: 365, citation: "40 CFR 266.502(f)(1)", jurisdiction: "FEDERAL" },
        },
        note: "Subpart P pharmaceuticals are scoped out of part 262 by 266.501(d), so the SAA caps " +
            "never attach; the one-year clock runs the same in a satellite or central area. Same " +
            "creditable/non-creditable over-strictness caveat as the central row.",
    },
    // A facility that does NOT operate under Subpart P (no recorded §11d
    // healthcare-facility activity, withdrawn, or a state that never adopted
    // the program) manages pharmaceutical waste under the ordinary generator
    // standards of part 262 — these rows restate the part 262 answers
    // deliberately, mirroring the ordinary rows above tier for tier, rather
    // than wildcarding generatorStatus the way the participating rows do.
    {
        when: {
            areaType: "CENTRAL",
            wasteKind: "PHARMACEUTICAL",
            operatesUnderSubpartP: false,
            generatorStatus: "VSQG",
        },
        expect: {
            clock: { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" },
        },
        note: PHARMACEUTICAL_NON_PARTICIPATING_NOTE,
    },
    {
        when: {
            areaType: "CENTRAL",
            wasteKind: "PHARMACEUTICAL",
            operatesUnderSubpartP: false,
            generatorStatus: "SQG",
            mustTransport200Miles: false,
        },
        expect: {
            clock: { kind: "TIME", days: 180, citation: "40 CFR 262.16(b)", jurisdiction: "FEDERAL" },
        },
        note: PHARMACEUTICAL_NON_PARTICIPATING_NOTE,
    },
    {
        when: {
            areaType: "CENTRAL",
            wasteKind: "PHARMACEUTICAL",
            operatesUnderSubpartP: false,
            generatorStatus: "SQG",
            mustTransport200Miles: true,
        },
        expect: {
            clock: { kind: "TIME", days: 270, citation: "40 CFR 262.16(d)", jurisdiction: "FEDERAL" },
        },
        note: PHARMACEUTICAL_NON_PARTICIPATING_NOTE,
    },
    {
        when: {
            areaType: "CENTRAL",
            wasteKind: "PHARMACEUTICAL",
            operatesUnderSubpartP: false,
            generatorStatus: "LQG",
        },
        expect: {
            clock: { kind: "TIME", days: 90, citation: "40 CFR 262.17(a)", jurisdiction: "FEDERAL" },
        },
        note: PHARMACEUTICAL_NON_PARTICIPATING_NOTE,
    },
    {
        when: {
            areaType: "CENTRAL",
            wasteKind: "PHARMACEUTICAL",
            operatesUnderSubpartP: false,
            generatorStatus: null,
        },
        expect: { clock: { kind: "UNDETERMINED" } },
        note: PHARMACEUTICAL_NON_PARTICIPATING_NOTE,
    },
    {
        when: {
            areaType: "SATELLITE",
            wasteKind: "PHARMACEUTICAL",
            operatesUnderSubpartP: false,
            generatorStatus: "SQG",
        },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: NO_STANDING_SATELLITE_CLOCK },
        note: PHARMACEUTICAL_NON_PARTICIPATING_NOTE,
    },
    {
        when: {
            areaType: "SATELLITE",
            wasteKind: "PHARMACEUTICAL",
            operatesUnderSubpartP: false,
            generatorStatus: "LQG",
        },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: NO_STANDING_SATELLITE_CLOCK },
        note: PHARMACEUTICAL_NON_PARTICIPATING_NOTE,
    },
    {
        when: {
            areaType: "SATELLITE",
            wasteKind: "PHARMACEUTICAL",
            operatesUnderSubpartP: false,
            generatorStatus: "VSQG",
        },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" },
            clock: NO_STANDING_SATELLITE_CLOCK,
        },
        note: PHARMACEUTICAL_NON_PARTICIPATING_NOTE,
    },
    {
        when: {
            areaType: "SATELLITE",
            wasteKind: "PHARMACEUTICAL",
            operatesUnderSubpartP: false,
            generatorStatus: null,
        },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: NO_STANDING_SATELLITE_CLOCK },
        note: PHARMACEUTICAL_NON_PARTICIPATING_NOTE,
    },
    // A universal-waste category the state never adopted leaves the container on
    // the ordinary generator standards, so these rows mirror the part 262 answers
    // tier for tier rather than wildcarding generatorStatus the way the managed
    // rows above do.
    {
        when: {
            areaType: "CENTRAL",
            wasteKind: "UNIVERSAL_WASTE",
            managedAsUniversalWaste: false,
            generatorStatus: "VSQG",
        },
        expect: {
            clock: { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" },
        },
        note: UNIVERSAL_WASTE_NOT_MANAGED_NOTE,
    },
    {
        when: {
            areaType: "CENTRAL",
            wasteKind: "UNIVERSAL_WASTE",
            managedAsUniversalWaste: false,
            generatorStatus: "SQG",
            mustTransport200Miles: false,
        },
        expect: {
            clock: { kind: "TIME", days: 180, citation: "40 CFR 262.16(b)", jurisdiction: "FEDERAL" },
        },
        note: UNIVERSAL_WASTE_NOT_MANAGED_NOTE,
    },
    {
        when: {
            areaType: "CENTRAL",
            wasteKind: "UNIVERSAL_WASTE",
            managedAsUniversalWaste: false,
            generatorStatus: "SQG",
            mustTransport200Miles: true,
        },
        expect: {
            clock: { kind: "TIME", days: 270, citation: "40 CFR 262.16(d)", jurisdiction: "FEDERAL" },
        },
        note: UNIVERSAL_WASTE_NOT_MANAGED_NOTE,
    },
    {
        when: {
            areaType: "CENTRAL",
            wasteKind: "UNIVERSAL_WASTE",
            managedAsUniversalWaste: false,
            generatorStatus: "LQG",
        },
        expect: {
            clock: { kind: "TIME", days: 90, citation: "40 CFR 262.17(a)", jurisdiction: "FEDERAL" },
        },
        note: UNIVERSAL_WASTE_NOT_MANAGED_NOTE,
    },
    {
        when: {
            areaType: "CENTRAL",
            wasteKind: "UNIVERSAL_WASTE",
            managedAsUniversalWaste: false,
            generatorStatus: null,
        },
        expect: { clock: { kind: "UNDETERMINED" } },
        note: UNIVERSAL_WASTE_NOT_MANAGED_NOTE,
    },
    {
        when: {
            areaType: "SATELLITE",
            wasteKind: "UNIVERSAL_WASTE",
            managedAsUniversalWaste: false,
            generatorStatus: "SQG",
        },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: NO_STANDING_SATELLITE_CLOCK },
        note: UNIVERSAL_WASTE_NOT_MANAGED_NOTE,
    },
    {
        when: {
            areaType: "SATELLITE",
            wasteKind: "UNIVERSAL_WASTE",
            managedAsUniversalWaste: false,
            generatorStatus: "LQG",
        },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: NO_STANDING_SATELLITE_CLOCK },
        note: UNIVERSAL_WASTE_NOT_MANAGED_NOTE,
    },
    {
        when: {
            areaType: "SATELLITE",
            wasteKind: "UNIVERSAL_WASTE",
            managedAsUniversalWaste: false,
            generatorStatus: "VSQG",
        },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" },
            clock: NO_STANDING_SATELLITE_CLOCK,
        },
        note: UNIVERSAL_WASTE_NOT_MANAGED_NOTE,
    },
    {
        when: {
            areaType: "SATELLITE",
            wasteKind: "UNIVERSAL_WASTE",
            managedAsUniversalWaste: false,
            generatorStatus: null,
        },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: NO_STANDING_SATELLITE_CLOCK },
        note: UNIVERSAL_WASTE_NOT_MANAGED_NOTE,
    },
];
/**
 * Federal RCRA generator accumulation limits, 40 CFR part 262 — and the
 * behavior every state class starts from (see `StateRules`).
 *
 * Every result stamps `jurisdiction` as a LITERAL matching the citation
 * beside it. Deliberately no `this.jurisdiction` member to DRY that up: an
 * inherited or `super.`-delegated method keeps the subclass's `this`, so a
 * federal answer reached through a state class would come back stamped with
 * the state's jurisdiction next to a CFR citation. Federal methods stamping
 * FEDERAL regardless of the instance is what keeps a state's inherited slots
 * honestly labeled.
 */
class FederalRules extends JurisdictionRules {
    constructor() {
        super(...arguments);
        this.expectations = FEDERAL_EXPECTATIONS;
    }
    centralClock(ctx) {
        // Universal waste (273.15(a), 273.35(a)) is scoped out of part 262
        // entirely, status-independent, and used oil (279) has no generator
        // storage time limit at all — both never fall through to the ordinary
        // RCRA clocks below. Pharmaceutical is different: 266.501(d) puts
        // Subpart P "in lieu of parts 262 through 265" ONLY at a facility that
        // operates under it (a recorded healthcare-facility activity, no
        // withdrawal, in an adopting state — see `operatesUnderSubpartP` on
        // RuleQuery). Everywhere else, pharmaceutical waste is ordinary
        // hazardous waste and falls through to the generator-tier clocks below
        // exactly like any other kind: this bucket's `if` deliberately isn't
        // paired with an unconditional `wasteKind === "PHARMACEUTICAL"` return.
        if (ctx.wasteKind === "UNIVERSAL_WASTE" && ctx.managedAsUniversalWaste) {
            // 273.15(a) is the small-quantity handler limit, 273.35(a) the
            // large-quantity handler limit — word for word identical. This engine
            // doesn't model handler category (set by UW quantity on hand, not
            // generator status), hence citing both together.
            return {
                kind: "TIME",
                days: 365,
                citation: "40 CFR 273.15(a), 273.35(a)",
                jurisdiction: "FEDERAL",
            };
        }
        if (ctx.wasteKind === "PHARMACEUTICAL" && ctx.operatesUnderSubpartP) {
            // The healthcare-facility limit for non-creditable pharmaceuticals.
            // Creditable pharmaceuticals have no facility accumulation clock at
            // all, but this engine's wasteKind doesn't distinguish creditable from
            // non-creditable, so applying one year to every PHARMACEUTICAL
            // container is over-strict but never under-strict.
            return { kind: "TIME", days: 365, citation: "40 CFR 266.502(f)(1)", jurisdiction: "FEDERAL" };
        }
        if (ctx.wasteKind === "USED_OIL") {
            // Part 279 sets no generator storage time limit — only a 35-day cap
            // for transfer facilities, which doesn't apply here.
            return { kind: "NO_LIMIT", citation: "40 CFR 279.22", jurisdiction: "FEDERAL" };
        }
        if (ctx.generatorStatus === "LQG") {
            return { kind: "TIME", days: 90, citation: "40 CFR 262.17(a)", jurisdiction: "FEDERAL" };
        }
        if (ctx.generatorStatus === "SQG") {
            if (ctx.mustTransport200Miles) {
                return { kind: "TIME", days: 270, citation: "40 CFR 262.16(d)", jurisdiction: "FEDERAL" };
            }
            return { kind: "TIME", days: 180, citation: "40 CFR 262.16(b)", jurisdiction: "FEDERAL" };
        }
        if (ctx.generatorStatus === "VSQG") {
            // A VSQG under 262.14 is not subject to 262.16/262.17's central time
            // limits, and 262.14 imposes none of its own; what constrains a VSQG is
            // the facility-wide on-site cap, which the facility rollup owns.
            return { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" };
        }
        return { kind: "UNDETERMINED" };
    }
    satelliteCaps(ctx) {
        // Universal waste and used oil are scoped out of part 262 entirely, so
        // the SAA volume caps and move window never attach regardless of
        // generator status. Pharmaceutical only gets that exemption AT A
        // SUBPART P FACILITY (see the comment on centralClock); otherwise it
        // falls through to the ordinary VSQG-carve-out logic below.
        if (ctx.wasteKind === "UNIVERSAL_WASTE" && ctx.managedAsUniversalWaste) {
            return { kind: "NO_LIMIT", citation: "40 CFR 261.9, 273.1(b)", jurisdiction: "FEDERAL" };
        }
        if (ctx.wasteKind === "USED_OIL") {
            return { kind: "NO_LIMIT", citation: "40 CFR 261.6(a)(4)", jurisdiction: "FEDERAL" };
        }
        if (ctx.wasteKind === "PHARMACEUTICAL" && ctx.operatesUnderSubpartP) {
            return { kind: "NO_LIMIT", citation: "40 CFR 266.501(d)", jurisdiction: "FEDERAL" };
        }
        if (ctx.generatorStatus === "VSQG") {
            // We read 262.15 as relief from central time limits a VSQG never had,
            // so the caps do not reach VSQGs. Unconfirmed by a regulatory
            // specialist — flagged in the SME packet.
            return { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" };
        }
        // An unknown status still gets the caps — the more protective default.
        return {
            kind: "VOLUME",
            nonAcuteGal: wasteAccumulation_1.SAA_NON_ACUTE_VOLUME_LIMIT_GAL,
            acuteGal: wasteAccumulation_1.SAA_ACUTE_VOLUME_LIMIT_GAL,
            acuteKg: wasteAccumulation_1.SAA_ACUTE_MASS_LIMIT_KG,
            moveWindowDays: wasteAccumulation_1.SATELLITE_MOVE_WINDOW_DAYS,
            citation: "40 CFR 262.15",
            jurisdiction: "FEDERAL",
        };
    }
    satelliteClock(ctx) {
        // Same one-year clocks as centralClock, same Subpart P participation
        // gate on pharmaceutical: these programs' time limits run regardless of
        // area type, unlike the ordinary satellite/central split below. Used oil
        // has no clock anywhere. A non-participating pharmaceutical container
        // falls through to the final NO_LIMIT return below — 262.15 sets no
        // standing satellite clock for ANY status, so there's no per-status
        // branch to fall into here the way centralClock has.
        if (ctx.wasteKind === "UNIVERSAL_WASTE" && ctx.managedAsUniversalWaste) {
            return {
                kind: "TIME",
                days: 365,
                citation: "40 CFR 273.15(a), 273.35(a)",
                jurisdiction: "FEDERAL",
            };
        }
        if (ctx.wasteKind === "PHARMACEUTICAL" && ctx.operatesUnderSubpartP) {
            return { kind: "TIME", days: 365, citation: "40 CFR 266.502(f)(1)", jurisdiction: "FEDERAL" };
        }
        if (ctx.wasteKind === "USED_OIL") {
            return { kind: "NO_LIMIT", citation: "40 CFR 279.22", jurisdiction: "FEDERAL" };
        }
        // 262.15 sets no standing clock on satellite accumulation — the only time
        // limit is the exceedance-triggered move window, carried on the caps.
        return { kind: "NO_LIMIT", citation: "40 CFR 262.15", jurisdiction: "FEDERAL" };
    }
}
exports.FederalRules = FederalRules;
/**
 * Behavior inherits; the spec never does. A state overrides only the slots
 * where its law departs from federal — inherited slots return FEDERAL-stamped
 * answers, the honest reading of "this state falls back to federal law here"
 * — but it must author its own complete expectations table, including rows
 * claiming those inherited federal answers. Were `expectations` inherited
 * too, a half-written state would pass the sweep with federal rows
 * masquerading as reviewed state claims.
 */
class StateRules extends FederalRules {
}
exports.StateRules = StateRules;
//# sourceMappingURL=FEDERAL.js.map